import { Card, CardContent, Typography, Box, MenuItem, Select } from "@mui/material";
import GenericTable from "../../Components/Back-End/Table";
import { useState, useEffect } from "react";
import SectionCard from "../../Components/Back-End/SectionCard";

export function BoardMembersTable() {
    const [validationErrors, setValidationErrors] = useState({});
    const [boardMemberTypes, setBoardMemberTypes] = useState([]);

    useEffect(() => {
        async function fetchBoardMemberTypes() {
            try {
                const res = await fetch('/api/boardmembertypes/GetBoardMemberTypes');
                if (!res.ok) throw new Error('Failed to fetch board member types');
                const data = await res.json();
                setBoardMemberTypes(data);
            } catch (error) {
                console.error(error);
            }
        }
        fetchBoardMemberTypes();
    }, []);

    const columns = [
        { accessorKey: "id", header: "ID", enableEditing: false },
        {
            accessorKey: "imagePath",
            header: "Image",
            Edit: ({ cell, row }) => {
                const [previewUrl, setPreviewUrl] = useState(null);
                const [error, setError] = useState("");
                const [initials, setInitials] = useState("");
                const [bgColor, setBgColor] = useState("#888888");
                const filename = cell.getValue();
                const existingUrl = filename ? `fsc-images/board-members/${filename}` : null;

                // Create avatar canvas and generate image blob
                const generateAvatar = () => {
                    if (!initials.trim()) {
                        setError("Please enter initials (max 4 characters)");
                        return;
                    }
                    if (initials.length > 4) {
                        setError("Initials must be 4 characters or less");
                        return;
                    }
                    setError("");

                    const canvas = document.createElement("canvas");
                    const size = 128;
                    canvas.width = size;
                    canvas.height = size;
                    const ctx = canvas.getContext("2d");

                    // Background circle
                    ctx.fillStyle = bgColor;
                    ctx.beginPath();
                    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
                    ctx.fill();

                    // Text
                    ctx.fillStyle = "#fff";
                    ctx.font = "bold 64px sans-serif";
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";
                    ctx.fillText(initials.toUpperCase(), size / 2, size / 2);

                    canvas.toBlob((blob) => {
                        if (!blob) {
                            setError("Failed to generate avatar");
                            return;
                        }
                        const file = new File([blob], "avatar.png", { type: "image/png" });
                        const url = URL.createObjectURL(blob);
                        setPreviewUrl(url);
                        row._valuesCache["imageFile"] = file;
                    });
                };

                const handleFileChange = (event) => {
                    const file = event.target.files[0];
                    if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                            setError("File size must be less than 5 MB");
                            event.target.value = "";
                            setPreviewUrl(null);
                            return;
                        }
                        setError("");
                        const reader = new FileReader();
                        reader.onloadend = () => setPreviewUrl(reader.result);
                        reader.readAsDataURL(file);
                        row._valuesCache["imageFile"] = file;
                    }
                };

                return (
                    <Box display="flex" flexDirection="column" gap={2}>
                        {(previewUrl || existingUrl) && (
                            <img
                                src={previewUrl || existingUrl}
                                alt="Preview"
                                style={{
                                    width: "80px",
                                    height: "80px",
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    border: "2px solid #ccc",
                                    flexShrink: 0,
                                    marginTop: "1rem",
                                    marginBottom: "1rem",
                                }}
                            />
                        )}

                        {/* Upload image */}
                        <Box>
                            <label style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                                Upload Image
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    style={{ display: "block", marginTop: "0.25rem" }}
                                />
                            </label>
                            {error && (
                                <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.25 }}>
                                    {error}
                                </Typography>
                            )}
                        </Box>

                        {/* OR generate avatar */}
                        <Box display="flex" alignItems="center" gap={1}>
                            <input
                                type="text"
                                maxLength={4}
                                placeholder="Initials (max 4)"
                                value={initials}
                                onChange={(e) => setInitials(e.target.value.toUpperCase())}
                                style={{
                                    width: 100,
                                    padding: "4px 8px",
                                    fontSize: "1rem",
                                    borderRadius: 4,
                                    border: "1px solid #ccc",
                                    textTransform: "uppercase",
                                }}
                            />
                            <input
                                type="color"
                                value={bgColor}
                                onChange={(e) => setBgColor(e.target.value)}
                                title="Pick background color"
                                style={{ width: 40, height: 40, border: "none", padding: 0, cursor: "pointer" }}
                            />
                            <button
                                type="button"
                                onClick={generateAvatar}
                                style={{
                                    padding: "6px 12px",
                                    backgroundColor: "#1976d2",
                                    color: "white",
                                    border: "none",
                                    borderRadius: 4,
                                    cursor: "pointer",
                                    fontWeight: "bold",
                                }}
                            >
                                Generate Avatar
                            </button>
                        </Box>
                    </Box>
                );
            },
            Cell: ({ cell }) => {
                const filename = cell.getValue();
                const imagePath = filename ? `fsc-images/board-members/${filename}` : "";
                return (
                    <img
                        src={imagePath}
                        alt="Board Member"
                        style={{
                            width: "60px",
                            height: "60px",
                            objectFit: "cover",
                            borderRadius: "50%",
                            border: "2px solid #ddd",
                        }}
                    />
                );
            },
        },
        {
            accessorKey: "name",
            header: "Name",
            validate: (value) => {
                if (!value || value.trim() === "") return "Board member name is required.";
            },
            muiEditTextFieldProps: () => ({
                required: true,
                error: !!validationErrors.name,
                helperText: validationErrors.name ?? '',
                onChange: () => setValidationErrors(prev => ({ ...prev, name: undefined })),
            }),
        },
        {
            accessorKey: "email",
            meta: { hideInTable: true },
            header: "Email",
            muiEditTextFieldProps: () => ({
                error: !!validationErrors.email,
                helperText: validationErrors.email ?? '',
                onChange: () => setValidationErrors(prev => ({ ...prev, email: undefined })),
                inputProps: { maxLength: 255 }, // limit to 255 characters
            }),
            validate: (value) => {
                if (!value || value.trim() === "") {
                    return; // ✅ Optional — no validation if empty
                }

                if (value.length > 255) {
                    return "Email must be less than 255 characters.";
                }

                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                    return "Please enter a valid email address.";
                }
            },
        },
        {
            accessorKey: "boardMemberTypeId",
            header: "Executive Type",
            editVariant: "select",
            editSelectOptions: boardMemberTypes.map(x => ({
                label: x.typeName,
                value: x.id
            })),
            muiEditTextFieldProps: () => ({
                required: true,
                select: true,
                error: !!validationErrors.boardMemberTypeId,
                helperText: validationErrors.boardMemberTypeId ?? '',
                onChange: () => setValidationErrors(prev => ({ ...prev, boardMemberTypeId: undefined })),
            }),
            validate: (value) => {
                if (value === undefined || value === null || value === "") {
                    return "Board member type is required.";
                }
            },
            Cell: ({ cell }) => {
                const typeId = cell.getValue();
                const type = boardMemberTypes.find(x => x.id === typeId);
                return type ? type.typeName : typeId;
            }
        },
        {
            accessorKey: "jobTitle",
            header: "Job Title",
            meta: { hideInTable: true },
            muiEditTextFieldProps: () => ({
                error: !!validationErrors.jobTitle,
                helperText: validationErrors.jobTitle ?? '',
                onChange: () => setValidationErrors(prev => ({ ...prev, jobTitle: undefined })),
            }),
        },
        {
            accessorKey: "sortOrder",
            header: "Sort Order",
            muiEditTextFieldProps: () => ({
                type: "number",
                required: true,
                error: !!validationErrors.sortOrder,
                helperText: validationErrors.sortOrder ?? '',
                onChange: () => setValidationErrors(prev => ({ ...prev, sortOrder: undefined })),
            }),
            validate: (value) => {
                if (value === undefined || value === null || value === "") {
                    return "Sort order is required.";
                }
                if (isNaN(value)) {
                    return "Sort order must be a number.";
                }
                if (Number(value) < 0) {
                    return "Sort order must be greater than or equal to 0.";
                }
            },
        },
        {
            accessorKey: "isActive",
            header: "Active",
            meta: { hideInTable: true },
            editVariant: "select",
            editSelectOptions: [
                { label: "Yes", value: true },
                { label: "No", value: false },
            ],
            muiEditTextFieldProps: () => ({
                select: true,
                required: true,
                defaultValue: true,
                error: !!validationErrors.isActive,
                helperText: validationErrors.isActive ?? '',
                onChange: () => setValidationErrors(prev => ({ ...prev, isActive: undefined })),
            }),
            validate: (value) => {
                if (value === undefined || value === null || value === "") {
                    return "Active status is required.";
                }
            },
            Cell: ({ cell }) => {
                const value = cell.getValue();
                return value ? "Yes" : "No";
            },
        },
    ];

    const columnVisibility = columns.reduce((acc, col) => {
        if (col.meta?.hideInTable) {
            acc[col.accessorKey] = false;
        }
        return acc;
    }, {});

    return (
        <SectionCard title="Executive Members" subtitle="Manage your Executive members below">
            <GenericTable
                columns={columns}
                itemName="Executive Member"
                createApi="/api/boardmembers/save"
                readApi="/api/boardmembers/GetBoardMembers"
                updateApi="/api/boardmembers/save"
                deleteApi="/api/boardmembers/delete"
                columnVisibility={columnVisibility}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
                publicSitePage="executives"
            />
        </SectionCard>
    );
}
